import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import '../Style/Science.css';

// Liste des ingrédients avec leurs descriptions et formules chimiques
const ingredientsData = [
  {
    name: 'Sucre (Saccharose)',
    formula: 'C₁₂H₂₂O₁₁',
    description:
      'Le sucre adoucit vos pâtisseries et joue un rôle clé dans la caramélisation et la réaction de Maillard, donnant des arômes et une belle couleur dorée.',
  },
  {
    name: 'Levure Chimique (Bicarbonate de Sodium)',
    formula: 'NaHCO₃',
    description:
      "Lorsqu'elle est combinée avec un acide, elle libère du dioxyde de carbone (CO₂) pour faire lever la pâte.",
  },
  {
    name: 'Farine de Blé (Amidon)',
    formula: '(C₆H₁₀O₅)n',
    description:
      "L'amidon dans la farine aide à la structure de la pâte, tandis que les protéines comme la gliadine et la gluténine forment le gluten, responsable de la texture élastique.",
  },
  {
    name: 'Beurre (Triglycérides)',
    formula: 'C₃H₈O₃ + Acides gras',
    description:
      'Le beurre est composé de triglycérides et il donne une texture tendre aux pâtisseries tout en apportant des arômes délicieux.',
  },
  {
    name: 'Oeufs (Protéines - Ovalbumine)',
    formula: 'C₄₈H₈₀N₁₆O₁₆S₈',
    description:
      'Les oeufs apportent des protéines et des lipides qui contribuent à la structure et à la texture des pâtisseries.',
  },
  {
    name: 'Lait (Lactose)',
    formula: 'C₁₂H₂₂O₁₁',
    description:
      "Le lait est une émulsion qui ajoute de l'humidité et de la douceur, tout en étant une source de protéines comme la caséine.",
  },
];

const reactionsData = [
  {
    title: 'La Réaction de Maillard',
    description:
      'Cette réaction entre les sucres et les protéines crée des saveurs complexes et une couleur dorée lors de la cuisson.',
  },
  {
    title: 'Caramélisation',
    description:
      "Lors de l'exposition du sucre à une chaleur intense, le sucre se décompose en différents composés, lui donnant une couleur dorée et un goût sucré intense.",
  },
  {
    title: 'Levée de la Pâte',
    description:
      "Le CO₂ produit par la levure chimique ou la levure de boulanger crée des bulles d'air qui rendent la pâte légère.",
  },
  {
    title: 'Coagulation des Protéines (oeufs)',
    description:
      "Les protéines contenues dans les oeufs se dénaturent sous l’effet de la chaleur et coagulent, donnant ainsi la texture des pâtisseries.",
  },
  {
    title: 'Hydratation du Gluten',
    description:
      "L'eau mélangée avec la farine forme du gluten, un réseau de protéines qui piège les gaz, donnant ainsi la texture aérée du pain et des pâtisseries.",
  },
];

const moleculeRecipes = [
  {
    name: 'Eau',
    formula: ['H₂', 'O'],
    description: 'Composant essentiel dans presque toutes les recettes de pâtisserie.',
  },
  {
    name: 'Dioxyde de Carbone',
    formula: ['C', 'O₂'],
    description: 'Produit lors de la fermentation ou en utilisant de la levure chimique.',
  },
  {
    name: 'Chlorure de Sodium',
    formula: ['Na', 'Cl'],
    description: 'Sel de table, utilisé pour rehausser le goût des préparations.',
  },
  {
    name: 'Glucose',
    formula: ['C₆', 'H₁₂', 'O₆'],
    description: 'Un sucre simple, présent dans de nombreux aliments sucrés.',
  },
  {
    name: 'Saccharose',
    formula: ['C₁₂', 'H₂₂', 'O₁₁'],
    description: 'Sucre de table utilisé dans les recettes pour ajouter de la douceur.',
  },
  {
    name: 'Acide Acétique',
    formula: ['C₂', 'H₄', 'O₂'],
    description: 'Composant du vinaigre, parfois utilisé dans des recettes pour sa propriété acide.',
  },
  // Ajouter d'autres molécules ici si besoin
];

// Composant pour afficher une molécule avec animation
const Molecule = ({ name, formula, description, onClick }) => (
  <motion.div
    className="molecule"
    initial={{ opacity: 0, y: 50 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.3 }}
    whileHover={{ scale: 1.1 }}
    whileTap={{ scale: 0.95 }}
    onClick={() => onClick('Les Molécules', name, formula.join(' + '), description)}
  >
    <h3>
      {name} ({formula.join(' + ')})
    </h3>
  </motion.div>
);

// Composant pour afficher un ingrédient avec animation
const Ingredient = ({ name, formula, description, onClick }) => (
  <motion.div
    className="ingredient"
    initial={{ opacity: 0, y: 50 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.3 }}
    whileHover={{ scale: 1.1 }}
    whileTap={{ scale: 0.95 }}
    onClick={() => onClick('Les Ingrédients', name, formula, description)}
  >
    <h3>
      {name} ({formula})
    </h3>
  </motion.div>
);

// Composant pour afficher une réaction chimique avec animation
const Reaction = ({ title, description, onClick }) => (
  <motion.div
    className="reaction"
    initial={{ opacity: 0, y: 50 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.3 }}
    whileHover={{ scale: 1.1 }}
    whileTap={{ scale: 0.95 }}
    onClick={() => onClick('Les Réactions Chimiques', title, null, description)}
  >
    <h3>{title}</h3>
  </motion.div>
);

const Science = () => {
  // On stocke les données du modal dans un objet
  const [modalData, setModalData] = useState(null);
  // État pour gérer l'affichage des sections : 'ingredients', 'reactions' ou 'molecules'
  const [activeSection, setActiveSection] = useState(null);

  const openModal = (sectionTitle, title, formulaOrDescription, description) => {
    setModalData({ sectionTitle, title, formulaOrDescription, description });
  };

  const closeModal = () => {
    setModalData(null);
  };

  const toggleSection = (section) => {
    setActiveSection(activeSection === section ? null : section);
  };

  return (
    <motion.div
      className="science-container"
      onClick={(e) => e.stopPropagation()}
      initial={{ scale: 0.5, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      exit={{ scale: 0.5, opacity: 0 }}
      transition={{ duration: 0.3 }}
    >
      <h1>La Science des Pâtisseries</h1>
      <p>Découvrez la magie chimique derrière vos recettes préférées !</p>

      {/* Section Ingrédients */}
      <section className="ingredient-section">
        <motion.h2
          onClick={() => toggleSection('ingredients')}
          style={{ cursor: 'pointer' }}
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
        >
          Les Ingrédients
        </motion.h2>
        <AnimatePresence>
          {activeSection === 'ingredients' && (
            <motion.div
              key="ingredients"
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 50 }}
              transition={{ duration: 0.3 }}
            >
              {ingredientsData.map((ingredient, index) => (
                <Ingredient
                  key={index}
                  name={ingredient.name}
                  formula={ingredient.formula}
                  description={ingredient.description}
                  onClick={openModal}
                />
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </section>

      {/* Section Réactions Chimiques */}
      <section className="reaction-section">
        <motion.h2
          onClick={() => toggleSection('reactions')}
          style={{ cursor: 'pointer' }}
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
        >
          Les Réactions Chimiques
        </motion.h2>
        <AnimatePresence>
          {activeSection === 'reactions' && (
            <motion.div
              key="reactions"
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 50 }}
              transition={{ duration: 0.3 }}
            >
              {reactionsData.map((reaction, index) => (
                <Reaction
                  key={index}
                  title={reaction.title}
                  description={reaction.description}
                  onClick={openModal}
                />
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </section>

      {/* Section Molécules */}
      <section className="molecule-section">
        <motion.h2
          onClick={() => toggleSection('molecules')}
          style={{ cursor: 'pointer' }}
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
        >
          Les Molécules
        </motion.h2>
        <AnimatePresence>
          {activeSection === 'molecules' && (
            <motion.div
              key="molecules"
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 50 }}
              transition={{ duration: 0.3 }}
            >
              {moleculeRecipes.map((molecule, index) => (
                <Molecule
                  key={index}
                  name={molecule.name}
                  formula={molecule.formula}
                  description={molecule.description}
                  onClick={openModal}
                />
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </section>

      {/* Modal */}
      <AnimatePresence>
        {modalData && (
          <motion.div
            className="modal-overlay"
            onClick={closeModal}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <motion.div
              className="modal-content"
              onClick={(e) => e.stopPropagation()}
              initial={{ scale: 0.5, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.5, opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              <button className="close-button" onClick={closeModal}>
                X
              </button>
              <h2>{modalData.sectionTitle}</h2>
              <h3>{modalData.title}</h3>
              {modalData.formulaOrDescription && <p>{modalData.formulaOrDescription}</p>}
              {modalData.description && <p>{modalData.description}</p>}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default Science;
